import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { TokenService } from '../services/token.service';

/**
 * An HTTP interceptor function that appends an Authorization header with a Bearer token
 * to outgoing HTTP requests if an access token is available.
 *
 * The interceptor retrieves the access token from the `TokenService` and, if it exists,
 * clones the original HTTP request to include the Authorization header. If no access
 * token is available, the original request proceeds unmodified.
 *
 * This function is designed to work as part of an HTTP client pipeline to handle
 * authenticated requests seamlessly.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const router = inject(Router);

  const accessToken = tokenService.getAccessToken();

  const authReq = accessToken
                  ? req.clone({
      setHeaders: {
        Authorization: `Bearer ${ accessToken }`,
      },
    })
                  : req;

  return next(authReq)
    .pipe(
      catchError((error: HttpErrorResponse) => {
        const is401 = error.status === 401;
        const isRefreshRequest = req.url.includes('/auth/refreshToken');
        const isLoginRequest = req.url.includes('/auth/login');

        if (!is401 || isRefreshRequest || isLoginRequest) {
          return throwError(() => error);
        }

        return tokenService.handleRefreshToken()
          .pipe(
            switchMap((newAccessToken: string) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${ newAccessToken }`,
                },
              });

              return next(retryReq);
            }),
            catchError((refreshError) => {
              tokenService.clearTokens();
              router.navigate(['/login']);
              return throwError(() => refreshError);
            }),
          );
      }),
    );
};
